import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, TouchableOpacity, RefreshControl } from 'react-native';
import { Text, Card, Searchbar, ActivityIndicator, Chip, Divider, FAB } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';

const ProveedoresScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterType, setFilterType] = useState('todos');

  const loadProveedores = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getProveedores();
      setProveedores(data);
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadProveedores();
    }, [loadProveedores])
  );

  const filteredProveedores = proveedores.filter(p => {
    const matchEstado = filterType === 'todos' || p.estado === filterType.replace('s', '');
    const query = searchQuery.toLowerCase().trim();
    const matchSearch = !query ||
      (p.nombre || '').toLowerCase().includes(query) ||
      (p.contacto || '').toLowerCase().includes(query) ||
      (p.email || '').toLowerCase().includes(query) ||
      (p.telefono || '').includes(query);
    return matchEstado && matchSearch;
  });

  const renderProveedorItem = ({ item }) => (
    <TouchableOpacity
      onPress={() => navigation.navigate('ProveedorDetalle', { proveedorId: item.id })}
    >
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.proveedorHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.proveedorName}>{item.nombre}</Text>
              {item.rtn && <Text style={styles.rtnText}>RTN: {item.rtn}</Text>}
            </View>
            <Chip
              mode="outlined"
              style={[
                styles.estadoChip,
                item.estado === 'activo' ? styles.chipActivo : styles.chipInactivo
              ]}
              textStyle={{ fontSize: 11 }}
            >
              {item.estado === 'activo' ? 'Activo' : 'Inactivo'}
            </Chip>
          </View>
          <Divider style={styles.divider} />
          <View style={styles.contactInfo}>
            {item.contacto && (
              <Text style={styles.contactName}>
                <Ionicons name="person" size={13} color="#666" /> {item.contacto}
              </Text>
            )}
            <Text style={styles.contactDetails}>
              {item.telefono && <><Ionicons name="call" size={13} color="#666" /> {item.telefono}</>}
              {item.telefono && item.email ? '  |  ' : ''}
              {item.email && <><Ionicons name="mail" size={13} color="#666" /> {item.email}</>}
            </Text>
          </View>
          {item.direccion && (
            <Text style={styles.direccionText} numberOfLines={1}>
              <Ionicons name="location" size={13} color="#888" /> {item.direccion}
            </Text>
          )}
        </Card.Content>
      </Card>
    </TouchableOpacity>
  );

  if (loading && !refreshing) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0066cc" />
        <Text style={styles.loaderText}>Cargando proveedores...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Searchbar
        placeholder="Buscar por nombre, contacto, email..."
        onChangeText={setSearchQuery}
        value={searchQuery}
        style={styles.searchBar}
      />

      <View style={styles.filterContainer}>
        {['todos', 'activos', 'inactivos'].map(f => (
          <Chip
            key={f}
            onPress={() => setFilterType(f)}
            style={[styles.filterChip, filterType === f && styles.filterChipActive]}
            textStyle={filterType === f ? styles.filterChipTextActive : styles.filterChipTextInactive}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Chip>
        ))}
      </View>

      <FlatList
        data={filteredProveedores}
        renderItem={renderProveedorItem}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 4 }} />}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadProveedores(); }} />
        }
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="business-outline" size={64} color="#ccc" />
            <Text style={styles.emptyText}>No se encontraron proveedores</Text>
          </View>
        }
      />

      <FAB
        icon="plus"
        style={[styles.fab, { bottom: 16 + insets.bottom }]}
        onPress={() => navigation.navigate('NuevoProveedor')}
        label="Nuevo"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  searchBar: { margin: 8, marginBottom: 0, elevation: 2 },
  filterContainer: { flexDirection: 'row', padding: 8 },
  filterChip: { marginRight: 8, backgroundColor: '#f0f0f0' },
  filterChipActive: { backgroundColor: '#4CAF50' },
  filterChipTextInactive: { color: '#333' },
  filterChipTextActive: { color: 'white', fontWeight: '600' },
  card: { marginHorizontal: 8, elevation: 2 },
  proveedorHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  proveedorName: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  rtnText: { fontSize: 12, color: '#888', marginTop: 2 },
  estadoChip: { height: 28 },
  chipActivo: { backgroundColor: '#d4edda' },
  chipInactivo: { backgroundColor: '#f8d7da' },
  divider: { marginVertical: 8 },
  contactInfo: { marginBottom: 4 },
  contactName: { fontWeight: 'bold', fontSize: 13, color: '#555', marginBottom: 2 },
  contactDetails: { fontSize: 13, color: '#666' },
  direccionText: { fontSize: 12, color: '#888', marginTop: 4 },
  loaderContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loaderText: { marginTop: 10, color: '#666' },
  listContainer: { padding: 4, paddingBottom: 80 },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, fontSize: 16, color: '#888' },
  fab: { position: 'absolute', right: 16, bottom: 16, backgroundColor: '#0066cc' },
});

export default ProveedoresScreen;
